<template>
<div class="xiannong-about-page">
    <!-- 田园风格Banner -->
    <div class="farm-banner">
        <div class="banner-content">
            <h1>关于闲农日季</h1>
            <p>记录田园生活 · 分享自然美好</p>
        </div>
        <div class="nature-elements">
            <div class="sun-circle"></div>
            <div class="cloud-small"></div>
            <div class="cloud-large"></div>
            <div class="bird-flying" v-for="i in 3" :key="i" :style="getBirdStyle(i)">🐦</div>
        </div>
    </div>

    <!-- 我的故事 -->
    <div class="section story-section">
        <div class="container">
            <div class="story-container">
                <div class="story-image">
                    <div class="garden-scene">
                        <div class="garden-bed-large">
                            <div class="plant-large tomato-large"></div>
                            <div class="plant-large cucumber-large"></div>
                            <div class="plant-large lettuce-large"></div>
                            <div class="soil-texture"></div>
                        </div>
                        <div class="scene-tags">
                            <span>🌱 自然耕种</span>
                            <span>🍅 有机蔬菜</span>
                            <span>🌾 田园生活</span>
                        </div>
                    </div>
                </div>
                <div class="story-content">
                    <h2 class="section-title">我的<span class="highlight">田园故事</span></h2>
                    <p class="section-subtitle">从城市到乡村的生活转变</p>
                    <div class="story-text">
                        <p>曾经，我也是城市中的忙碌上班族。直到有一天，我选择了不一样的生活方式——回到乡村，开始了我的田园生活。</p>
                        <p>在这片土地上，我学习耕种、记录四季、感受自然的节奏。每一次播种都是希望的开始，每一次收获都是生活的馈赠。</p>
                        <p>这个网站就是我的田园日记，记录着每一天的农事、每一次的收获，还有那些与自然相处的美好时光。</p>
                    </div>
                    <div class="story-stats">
                        <div class="story-stat" v-for="stat in lifeStats" :key="stat.label">
                            <div class="stat-icon">{{ stat.icon }}</div>
                            <div class="stat-number">{{ stat.number }}</div>
                            <div class="stat-label">{{ stat.label }}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- 田园理念 -->
    <div class="section philosophy-section">
        <div class="container">
            <h2 class="section-title">田园<span class="highlight">生活理念</span></h2>
            <p class="section-subtitle">回归自然，体验本真</p>
            <div class="philosophy-grid">
                <div class="philosophy-card" v-for="(item, index) in philosophies" :key="index">
                    <div class="philosophy-icon" :style="{backgroundColor: item.color}">
                        <div class="icon-symbol">{{ item.symbol }}</div>
                    </div>
                    <h3>{{ item.title }}</h3>
                    <p>{{ item.description }}</p>
                </div>
            </div>
        </div>
    </div>

    <!-- 日常作息 -->
    <div class="section routine-section">
        <div class="container">
            <h2 class="section-title">田园<span class="highlight">日常作息</span></h2>
            <p class="section-subtitle">跟随自然节奏的生活</p>
            <div class="timeline">
                <div class="timeline-item" v-for="(item, index) in dailyRoutine" :key="index">
                    <div class="timeline-time">{{ item.time }}</div>
                    <div class="timeline-content">
                        <div class="timeline-icon">{{ item.icon }}</div>
                        <div class="timeline-text">
                            <h4>{{ item.title }}</h4>
                            <p>{{ item.description }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- 种植记录 -->
    <div class="section planting-section">
        <div class="container">
            <h2 class="section-title">年度<span class="highlight">种植记录</span></h2>
            <p class="section-subtitle">每一粒种子都是希望</p>
            <div class="planting-chart">
                <div class="planting-item" v-for="plant in plantingRecords" :key="plant.name">
                    <div class="plant-header">
                        <div class="plant-icon">{{ plant.icon }}</div>
                        <div class="plant-name">{{ plant.name }}</div>
                    </div>
                    <div class="plant-details">
                        <div class="detail-item">
                            <span class="detail-label">播种时间</span>
                            <span class="detail-value">{{ plant.plantingTime }}</span>
                        </div>
                        <div class="detail-item">
                            <span class="detail-label">收获时间</span>
                            <span class="detail-value">{{ plant.harvestTime }}</span>
                        </div>
                        <div class="detail-item">
                            <span class="detail-label">总产量</span>
                            <span class="detail-value">{{ plant.yield }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- 四季感悟 -->
    <div class="section reflection-section">
        <div class="container">
            <h2 class="section-title">四季<span class="highlight">生活感悟</span></h2>
            <div class="reflection-cards">
                <div class="reflection-card" v-for="reflection in reflections" :key="reflection.season">
                    <div class="season-header" :style="{background: reflection.color}">
                        <div class="season-symbol">{{ reflection.symbol }}</div>
                        <h3>{{ reflection.season }}</h3>
                    </div>
                    <div class="reflection-content">
                        <p>{{ reflection.reflection }}</p>
                        <div class="reflection-stats">
                            <span>📝 {{ reflection.diaries }}篇日记</span>
                            <span>📸 {{ reflection.photos }}张照片</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>
</template>

<script>
export default {
    name: 'XiannongAboutPage',
    data() {
        return {
            lifeStats: [
                { icon: '🏠', number: '3年', label: '田园生活' },
                { icon: '🌱', number: '24种', label: '种植作物' },
                { icon: '📖', number: '486篇', label: '生活日记' },
                { icon: '📸', number: '2.3K张', label: '田园照片' }
            ],
            philosophies: [
                {
                    symbol: '🌱',
                    title: '自然耕种',
                    description: '遵循自然规律，不使用化肥农药，让土地保持最自然的状态',
                    color: '#C8E6C9'
                },
                {
                    symbol: '🍎',
                    title: '健康饮食',
                    description: '吃自己种的蔬菜，享受食物最原始、最健康的味道',
                    color: '#FFCDD2'
                },
                {
                    symbol: '🌞',
                    title: '简朴生活',
                    description: '物质极简，精神丰盈，追求内心的平静与满足',
                    color: '#FFF9C4'
                },
                {
                    symbol: '❤️',
                    title: '分享快乐',
                    description: '把田园的收获和美好分享给更多人，传递生活的温暖',
                    color: '#FFCCBC'
                }
            ],
            dailyRoutine: [
                {
                    time: '5:30 AM',
                    icon: '🌅',
                    title: '清晨起床',
                    description: '在鸟鸣声中醒来，迎接第一缕阳光'
                },
                {
                    time: '6:00 AM',
                    icon: '💦',
                    title: '晨间农事',
                    description: '为菜园浇水，检查蔬菜生长情况'
                },
                {
                    time: '8:00 AM',
                    icon: '🍳',
                    title: '田园早餐',
                    description: '用自己种的蔬菜准备营养早餐'
                },
                {
                    time: '9:00 AM',
                    icon: '✍️',
                    title: '记录日记',
                    description: '记录前一天的农事和感悟'
                },
                {
                    time: '2:00 PM',
                    icon: '🌿',
                    title: '午后劳作',
                    description: '除草、松土、施肥等农事工作'
                },
                {
                    time: '5:00 PM',
                    icon: '🧺',
                    title: '收获蔬菜',
                    description: '采摘成熟的蔬菜，为晚餐做准备'
                },
                {
                    time: '7:00 PM',
                    icon: '📖',
                    title: '晚间阅读',
                    description: '阅读农业书籍，学习新知识'
                },
                {
                    time: '9:00 PM',
                    icon: '✨',
                    title: '星空入眠',
                    description: '在星空下结束一天的田园生活'
                }
            ],
            plantingRecords: [
                {
                    icon: '🍅',
                    name: '西红柿',
                    plantingTime: '3月-4月',
                    harvestTime: '6月-9月',
                    yield: '18.5kg'
                },
                {
                    icon: '🥒',
                    name: '黄瓜',
                    plantingTime: '4月-5月',
                    harvestTime: '6月-8月',
                    yield: '15.2kg'
                },
                {
                    icon: '🥬',
                    name: '生菜',
                    plantingTime: '全年可种',
                    harvestTime: '30-45天',
                    yield: '9.8kg'
                },
                {
                    icon: '🥕',
                    name: '胡萝卜',
                    plantingTime: '3月-4月',
                    harvestTime: '7月-8月',
                    yield: '6.5kg'
                },
                {
                    icon: '🌶️',
                    name: '辣椒',
                    plantingTime: '3月-4月',
                    harvestTime: '7月-10月',
                    yield: '4.2kg'
                },
                {
                    icon: '🍓',
                    name: '草莓',
                    plantingTime: '9月-10月',
                    harvestTime: '次年4月-6月',
                    yield: '3.8kg'
                }
            ],
            reflections: [
                {
                    season: '春',
                    symbol: '🌸',
                    color: 'linear-gradient(135deg, #C8E6C9, #A5D6A7)',
                    reflection: '春天是播种希望的季节，看着种子破土而出，感受生命的奇迹',
                    diaries: '126',
                    photos: '423'
                },
                {
                    season: '夏',
                    symbol: '🌞',
                    color: 'linear-gradient(135deg, #FFF9C4, #FFE082)',
                    reflection: '夏天是生长的季节，万物繁荣，也是采摘初果的喜悦时刻',
                    diaries: '98',
                    photos: '567'
                },
                {
                    season: '秋',
                    symbol: '🍂',
                    color: 'linear-gradient(135deg, #FFE0B2, #FFCC80)',
                    reflection: '秋天是收获的季节，丰收的喜悦让所有的辛劳都变得值得',
                    diaries: '142',
                    photos: '489'
                },
                {
                    season: '冬',
                    symbol: '❄️',
                    color: 'linear-gradient(135deg, #B3E5FC, #81D4FA)',
                    reflection: '冬天是休养生息的季节，整理工具，规划来年的种植计划',
                    diaries: '120',
                    photos: '312'
                }
            ]
        }
    },
    methods: {
        getBirdStyle() {
            const left = Math.random() * 90
            const top = 20 + Math.random() * 40
            const delay = Math.random() * 2
            
            return {
                left: `${left}%`,
                top: `${top}%`,
                animationDelay: `${delay}s`
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.xiannong-about-page {
    font-family: 'Microsoft YaHei', 'PingFang SC', sans-serif;
    color: #5a3921;
    background: linear-gradient(to bottom, #f9f7f0 0%, #e8f5e8 100%);
    min-height: 100vh;
}

.farm-banner {
    height: 400px;
    background: linear-gradient(135deg, #a8e6cf 0%, #dcedc1 50%, #ffd3b6 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    position: relative;
    overflow: hidden;

    .banner-content {
        position: relative;
        z-index: 3;
        
        h1 {
            font-size: 48px;
            margin: 0 0 15px;
            font-weight: 600;
            color: #2e7d32;
            text-shadow: 2px 2px 4px rgba(255, 255, 255, 0.8);
            font-family: 'KaiTi', 'STKaiti', serif;
        }

        p {
            font-size: 20px;
            margin: 0;
            color: #558b2f;
            opacity: 0.9;
        }
    }

    .nature-elements {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 2;
        pointer-events: none;

        .sun-circle {
            position: absolute;
            top: 15%;
            right: 10%;
            width: 80px;
            height: 80px;
            background: radial-gradient(circle, #FFEB3B, #FF9800);
            border-radius: 50%;
            box-shadow: 0 0 50px rgba(255, 152, 0, 0.4);
            animation: sunGlow 8s infinite ease-in-out;
        }

        .cloud-small, .cloud-large {
            position: absolute;
            background: rgba(255, 255, 255, 0.9);
            border-radius: 50px;
            animation: cloudFloat 20s infinite linear;
        }

        .cloud-small {
            top: 25%;
            left: 15%;
            width: 60px;
            height: 30px;
            animation-delay: 2s;

            &::before {
                content: '';
                position: absolute;
                width: 30px;
                height: 30px;
                background: rgba(255, 255, 255, 0.9);
                border-radius: 50%;
                top: -10px;
                left: 5px;
            }
        }

        .cloud-large {
            top: 35%;
            left: 60%;
            width: 100px;
            height: 40px;
            animation-delay: 5s;

            &::before, &::after {
                content: '';
                position: absolute;
                background: rgba(255, 255, 255, 0.9);
                border-radius: 50%;
            }

            &::before {
                width: 40px;
                height: 40px;
                top: -15px;
                left: 10px;
            }

            &::after {
                width: 50px;
                height: 50px;
                top: -20px;
                right: 10px;
            }
        }

        .bird-flying {
            position: absolute;
            font-size: 24px;
            animation: birdFly 10s infinite linear;
        }
    }
}

@keyframes sunGlow {
    0%, 100% {
        transform: scale(1);
        opacity: 0.8;
    }
    50% {
        transform: scale(1.1);
        opacity: 1;
    }
}

@keyframes cloudFloat {
    0% {
        transform: translateX(0);
    }
    50% {
        transform: translateX(30px);
    }
    100% {
        transform: translateX(0);
    }
}

@keyframes birdFly {
    0% {
        transform: translateX(0) scaleX(1);
    }
    50% {
        transform: translateX(200px) scaleX(1);
    }
    51% {
        transform: translateX(200px) scaleX(-1);
    }
    100% {
        transform: translateX(0) scaleX(-1);
    }
}

.section {
    padding: 80px 0;

    &.story-section {
        background: #f9f7f0;
    }

    &.philosophy-section {
        background: white;
    }

    &.routine-section {
        background: #f1f8e9;
    }

    &.planting-section {
        background: white;
    }

    &.reflection-section {
        background: #f9f7f0;
    }
}

.container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
}

.section-title {
    font-size: 36px;
    color: #5a3921;
    margin-bottom: 10px;
    font-weight: 600;
    text-align: center;

    .highlight {
        color: #4caf50;
        position: relative;
        
        &::after {
            content: '';
            position: absolute;
            bottom: 2px;
            left: 0;
            width: 100%;
            height: 6px;
            background: rgba(129, 199, 132, 0.3);
            z-index: -1;
            border-radius: 3px;
        }
    }
}

.section-subtitle {
    text-align: center;
    font-size: 18px;
    color: #8d6e63;
    margin-bottom: 50px;
}

.story-container {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 50px;
    align-items: center;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 30px;
    }
}

.story-image {
    .garden-scene {
        position: relative;
        
        .garden-bed-large {
            width: 100%;
            height: 300px;
            background: linear-gradient(to bottom, #8D6E63, #5D4037);
            border-radius: 15px;
            position: relative;
            overflow: hidden;
            box-shadow: 0 15px 40px rgba(139, 195, 74, 0.2);

            .soil-texture {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                background-image: 
                    radial-gradient(circle at 25% 25%, rgba(255,255,255,0.1) 2px, transparent 3px),
                    radial-gradient(circle at 75% 75%, rgba(255,255,255,0.1) 2px, transparent 3px);
                background-size: 40px 40px;
            }

            .plant-large {
                position: absolute;
                bottom: 0;

                &.tomato-large {
                    left: 20%;
                    width: 80px;
                    height: 100px;
                    background: #FF5252;
                    border-radius: 50% 50% 50% 50% / 60% 60% 40% 40%;
                    animation: plantGrow 4s infinite ease-in-out;

                    &::after {
                        content: '';
                        position: absolute;
                        top: -25px;
                        left: 50%;
                        transform: translateX(-50%);
                        width: 60px;
                        height: 60px;
                        background: #FF5252;
                        border-radius: 50%;
                        box-shadow: 0 0 0 12px rgba(255, 82, 82, 0.3);
                    }
                }

                &.cucumber-large {
                    left: 45%;
                    width: 40px;
                    height: 80px;
                    background: #8BC34A;
                    border-radius: 20px;
                    animation: plantGrow 4s infinite ease-in-out 0.5s;

                    &::after {
                        content: '';
                        position: absolute;
                        top: -20px;
                        left: 0;
                        width: 100%;
                        height: 40px;
                        background: #8BC34A;
                        border-radius: 20px;
                    }
                }

                &.lettuce-large {
                    left: 70%;
                    width: 70px;
                    height: 60px;
                    background: #4CAF50;
                    border-radius: 50%;
                    animation: plantGrow 4s infinite ease-in-out 1s;

                    &::before {
                        content: '';
                        position: absolute;
                        top: -20px;
                        left: 50%;
                        transform: translateX(-50%);
                        width: 60px;
                        height: 50px;
                        background: #4CAF50;
                        border-radius: 50%;
                    }
                }
            }
        }

        .scene-tags {
            display: flex;
            flex-wrap: wrap;
            gap: 10px;
            margin-top: 20px;
            justify-content: center;

            span {
                background: #f1f8e9;
                padding: 8px 20px;
                border-radius: 25px;
                font-size: 16px;
                color: #4caf50;
                border: 2px solid #c8e6c9;
                display: flex;
                align-items: center;
                gap: 8px;
            }
        }
    }
}

@keyframes plantGrow {
    0%, 100% {
        transform: translateY(0);
    }
    50% {
        transform: translateY(-10px);
    }
}

.story-text {
    color: #8d6e63;
    line-height: 1.8;
    margin-bottom: 30px;
    font-size: 16px;

    p {
        margin-bottom: 15px;
    }
}

.story-stats {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
    margin-top: 40px;

    @media (max-width: 480px) {
        grid-template-columns: 1fr;
    }
}

.story-stat {
    background: white;
    border-radius: 12px;
    padding: 25px;
    text-align: center;
    box-shadow: 0 5px 20px rgba(139, 195, 74, 0.1);
    transition: all 0.3s ease;

    &:hover {
        transform: translateY(-5px);
        box-shadow: 0 10px 25px rgba(139, 195, 74, 0.15);
    }

    .stat-icon {
        font-size: 36px;
        margin-bottom: 10px;
    }

    .stat-number {
        font-size: 28px;
        font-weight: 700;
        color: #4caf50;
        margin-bottom: 5px;
    }

    .stat-label {
        font-size: 14px;
        color: #8d6e63;
    }
}

.philosophy-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 30px;
    margin-top: 40px;
}

.philosophy-card {
    background: white;
    border-radius: 12px;
    padding: 30px;
    text-align: center;
    box-shadow: 0 5px 20px rgba(139, 195, 74, 0.1);
    transition: all 0.3s ease;

    &:hover {
        transform: translateY(-5px);
        box-shadow: 0 10px 25px rgba(139, 195, 74, 0.15);
    }

    .philosophy-icon {
        width: 80px;
        height: 80px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto 20px;

        .icon-symbol {
            font-size: 40px;
        }
    }

    h3 {
        font-size: 20px;
        margin: 0 0 15px;
        color: #5a3921;
    }

    p {
        color: #8d6e63;
        font-size: 14px;
        line-height: 1.6;
        margin: 0;
    }
}

.timeline {
    max-width: 800px;
    margin: 40px auto 0;
    position: relative;

    &::before {
        content: '';
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
        width: 4px;
        height: 100%;
        background: linear-gradient(to bottom, #a5d6a7, #ffd3b6);
        border-radius: 2px;
    }

    .timeline-item {
        margin-bottom: 40px;
        position: relative;
        width: 50%;

        &:nth-child(odd) {
            left: 0;
            padding-right: 60px;
            text-align: right;
        }

        &:nth-child(even) {
            left: 50%;
            padding-left: 60px;
        }

        .timeline-time {
            font-size: 18px;
            font-weight: 600;
            color: #4caf50;
            margin-bottom: 10px;
            display: flex;
            align-items: center;
            gap: 10px;

            &::before {
                content: '';
                position: absolute;
                top: 5px;
                width: 20px;
                height: 20px;
                background: white;
                border: 4px solid #4caf50;
                border-radius: 50%;
                z-index: 1;
            }
        }

        &:nth-child(odd) .timeline-time {
            justify-content: flex-end;

            &::before {
                right: -10px;
            }
        }

        &:nth-child(even) .timeline-time {
            justify-content: flex-start;

            &::before {
                left: -10px;
            }
        }

        .timeline-content {
            background: white;
            border-radius: 12px;
            padding: 20px;
            box-shadow: 0 5px 20px rgba(139, 195, 74, 0.1);
            transition: all 0.3s ease;

            &:hover {
                transform: translateX(5px);
                box-shadow: 0 10px 25px rgba(139, 195, 74, 0.15);
            }

            .timeline-icon {
                font-size: 32px;
                margin-bottom: 15px;
            }

            h4 {
                font-size: 18px;
                margin: 0 0 10px;
                color: #5a3921;
            }

            p {
                color: #8d6e63;
                font-size: 14px;
                line-height: 1.6;
                margin: 0;
            }
        }
    }
}

.planting-chart {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 30px;
    margin-top: 40px;
}

.planting-item {
    background: white;
    border-radius: 12px;
    padding: 25px;
    box-shadow: 0 5px 20px rgba(139, 195, 74, 0.1);
    transition: all 0.3s ease;

    &:hover {
        transform: translateY(-5px);
        box-shadow: 0 10px 25px rgba(139, 195, 74, 0.15);
    }

    .plant-header {
        display: flex;
        align-items: center;
        gap: 15px;
        margin-bottom: 20px;

        .plant-icon {
            font-size: 36px;
        }

        .plant-name {
            font-size: 20px;
            font-weight: 600;
            color: #5a3921;
        }
    }

    .plant-details {
        .detail-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 10px 0;
            border-bottom: 1px solid #f1f8e9;

            &:last-child {
                border-bottom: none;
            }

            .detail-label {
                color: #8d6e63;
                font-size: 14px;
            }

            .detail-value {
                color: #4caf50;
                font-weight: 600;
                font-size: 15px;
            }
        }
    }
}

.reflection-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 30px;
    margin-top: 40px;
}

.reflection-card {
    background: white;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 5px 20px rgba(139, 195, 74, 0.1);
    transition: all 0.3s ease;

    &:hover {
        transform: translateY(-5px);
        box-shadow: 0 10px 25px rgba(139, 195, 74, 0.15);
    }

    .season-header {
        padding: 25px;
        text-align: center;
        color: white;

        .season-symbol {
            font-size: 48px;
            margin-bottom: 10px;
        }

        h3 {
            font-size: 24px;
            margin: 0;
            font-weight: 600;
            text-shadow: 1px 1px 2px rgba(0,0,0,0.2);
        }
    }

    .reflection-content {
        padding: 25px;

        p {
            color: #8d6e63;
            line-height: 1.8;
            margin-bottom: 20px;
            font-size: 14px;
        }

        .reflection-stats {
            display: flex;
            justify-content: space-between;
            font-size: 12px;
            color: #a1887f;

            span {
                display: flex;
                align-items: center;
                gap: 5px;
            }
        }
    }
}

@media (max-width: 768px) {
    .farm-banner {
        height: 300px;
        
        h1 {
            font-size: 36px;
        }

        p {
            font-size: 18px;
        }

        .sun-circle {
            width: 50px;
            height: 50px;
            right: 5%;
        }
    }

    .section {
        padding: 60px 0;
    }

    .section-title {
        font-size: 28px;
    }

    .section-subtitle {
        font-size: 16px;
        margin-bottom: 30px;
    }

    .story-container {
        grid-template-columns: 1fr;
    }

    .timeline {
        &::before {
            left: 30px;
        }

        .timeline-item {
            width: 100%;
            left: 0 !important;
            padding-left: 60px !important;
            padding-right: 0 !important;
            text-align: left !important;

            .timeline-time {
                justify-content: flex-start !important;

                &::before {
                    left: -40px !important;
                }
            }
        }
    }
}

@media (max-width: 480px) {
    .farm-banner {
        height: 250px;
        
        h1 {
            font-size: 28px;
        }

        p {
            font-size: 16px;
        }
    }

    .philosophy-grid,
    .reflection-cards {
        grid-template-columns: 1fr;
    }

    .planting-chart {
        grid-template-columns: 1fr;
    }
}
</style>